// ThemeContext.js
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import COLOR_MAP from '../Components/themeCustomize/colorMap';
// Helper function to resolve CSS variables
const resolveColor = (colorValue) => {
    if (!colorValue) return '#0B1F47';
    if (colorValue.startsWith('var(')) {
        const varName = colorValue.replace('var(', '').replace(')', '').trim();
        return COLOR_MAP[varName] || colorValue;
    }
    return colorValue;
};
const defaultLightTheme = {
    name: 'Default light',
    type: 'light',
    colors: {
        /* Panel Colors */
        '--panel-left-bar': resolveColor('var(--surface-panel)'),
        '--top-bar':  resolveColor('var(--surface-primary)'),
        '--bottom-bar':  resolveColor('var(--surface-primary)'),
        /* Text Colors */
        '--color-headings': resolveColor('var(--text-headings)'),
        '--color-headings-blue': resolveColor('var(--text-headings-blue)'),
        '--color-body': resolveColor('var(--text-body)'),
        '--color-body-dark-mode': resolveColor('var(--text-body-dark-mode)'),
        '--color-light': resolveColor('var(--text-light)'),
        '--color-action': resolveColor('var(--text-action)'),
        '--color-action-hover':resolveColor('var(--text-action-hover)'),
        '--color-disabled': resolveColor('var(--text-disabled)'),
        '--color-information':resolveColor('var(--text-information)'),
        '--color-success': resolveColor('var(--text-success)'),
        '--color-warning': resolveColor('var(--text-warning)'),
        '--color-error': resolveColor('var(--text-error)'),
        '--color-on-action':  resolveColor('var(--text-on-action)'),
        '--color-on-disabled': resolveColor('var(--text-on-disabled)'), 

        /* Surface Colors */
        '--surface-page':  resolveColor('var(--surface-page)'),
        '--surface-notification':resolveColor('var(--surface-notification)'),
        '--surface-panel': resolveColor('var(--surface-panel)'),
        '--surface-light': resolveColor('var(--surface-light)'),
        '--surface-dark': resolveColor('var(--surface-dark)'),
        '--surface-dark-chat': resolveColor('var(--surface-dark-chat)'),
        "--surface-panel-dark":  resolveColor('var(--surface-panel-dark)'),
        "--surface-dark-top":  resolveColor('var(--surface-dark-top)'),
         '--surface-dark-2': resolveColor('var(--surface-dark-2)'),
        '--surface-neutral': resolveColor('var(--surface-neutral)'),
        '--surface-hover': resolveColor('var(--surface-hover)'),
        '--surface-primary': resolveColor('var(--surface-primary)'),
        '--surface-success': resolveColor('var(--surface-success)'),
        '--surface-disabled': resolveColor('var(--surface-disabled)'),
        '--surface-warning': resolveColor('var(--surface-warning)'),
        '--surface-error': resolveColor('var(--surface-error)'),
        '--surface-information': resolveColor('var(--surface-information)'),
        '--surface-action': resolveColor('var(--surface-action)'),
        '--surface-action-hover': resolveColor('var(--surface-action-hover)'),
        '--surface-page-dark': resolveColor('var(--surface-page-dark)'),


        /* Button Colors */
        '--btn-primary': resolveColor('var(--btn-primary)'),
        '--btn-primary-dm': resolveColor('var(--btn-primary-dm)'),
        '--btn-hover': resolveColor('var(--btn-hover)'),
        '--btn-active':resolveColor('var(--btn-active)'),
        '--btn-unactive': resolveColor('var(--btn-unactive)'),
        '--btn-information': resolveColor('var(--btn-information)'),
        '--btn-success': resolveColor('var(--btn-success)'),
        '--btn-warning': resolveColor('var(--btn-warning)'),
        '--btn-error': resolveColor('var(--btn-error)'),

        /* Border Colors */
        '--border-primary': resolveColor('var(--border-primary)'),
        '--border-intermediary': resolveColor('var(--border-intermediary)'),
        '--border-information': resolveColor('var(--border-information)'),
        '--border-success': resolveColor('var(--border-success)'),
        '--border-warning': resolveColor('var(--border-warning)'),
        '--border-error': resolveColor('var(--border-error)'),
        '--border-disabled': resolveColor('var(--border-disabled)'),
        '--border-action': resolveColor('var(--border-action)'),
        '--border-action-hover': resolveColor('var(--border-action-hover)'),
        '--border-focus': resolveColor('var(--border-focus)'),
        '--border-light': resolveColor('var(--border-light)'),

        /* Chat Bubble */
        '--chat-bubble': resolveColor('var(--chat-bubble)'),
         '--chat-bubble-dark': resolveColor('var(--chat-bubble-dark)'),

        /* Tag Colors */
        '--tag-01': resolveColor('var(--tag-01)'),
        '--tag-02':resolveColor('var(--tag-02)'),
        '--tag-03': resolveColor('var(--tag-03)'),
        '--tag-04': resolveColor('var(--tag-04)'),
        '--tag-05': resolveColor('var(--tag-05)'),
        '--tag-06': resolveColor('var(--tag-06)'),
        '--color-dark-hover':  resolveColor('var(--color-dark-hover)'),

        /* Legacy Mappings (for compatibility) */
        '--primary-main':  resolveColor('var(--primary-main)'),
        '--primary-contrastText':  resolveColor('var(--primary-contrastText)'), 
        '--secondary-main': resolveColor('var(--secondary-main)'), 
        '--secondary-contrastText': resolveColor('var(--secondary-contrastText)'),
        '--background-body':  resolveColor('var(--background-body)'),
        '--background-surface': resolveColor('var(--background-surface)'),
        '--background-muted': resolveColor('var(--background-muted)'),

        '--text-primary': resolveColor('var(--text-body)'),
        '--text-headings': resolveColor('var(--text-headings)'),
        '--text-headings-blue': resolveColor('var(--text-headings-blue)'),
        '--text-body': resolveColor('var(--text-body)'),
        '--text-body-dark': resolveColor('var(--text-body-dark)'),
        '--text-light': resolveColor('var(--text-light)'),
        '--text-action': resolveColor('var(--text-action)'),
        '--text-action-hover':resolveColor('var(--text-action-hover)'),
        '--text-disabled': resolveColor('var(--text-disabled)'),
        '--text-information':resolveColor('var(--text-information)'),
        '--text-success': resolveColor('var(--text-success)'),
        '--text-warning': resolveColor('var(--text-warning)'),
        '--text-error': resolveColor('var(--text-error)'),
        '--text-on-action':  resolveColor('var(--text-on-action)'),
        '--text-on-disabled': resolveColor('var(--text-on-disabled)'),

        '--text-profile-name': resolveColor('var(--text-profile-name)'),

        '--btn-primary-background': resolveColor('var(--btn-primary-background)'),
        '--btn-primary-text': resolveColor('var(--btn-primary-text)'),
        '--btn-primary-hover': resolveColor('var(--btn-primary-hover)'),
        '--btn-primary-disabled': resolveColor('var(--btn-primary-disabled)'),
        '--btn-secondary-background': resolveColor('var(--btn-secondary-background)'),
        '--btn-secondary-text': resolveColor('var(--btn-secondary-text)'),
        '--btn-secondary-hover': resolveColor('var(--btn-secondary-hover)'),
        '--btn-secondary-disabled': resolveColor('var(--btn-secondary-disabled)'),
        '--alert-success': resolveColor('var(--alert-success)'),
        '--alert-error': resolveColor('var(--alert-error)'),
        '--alert-warning': resolveColor('var(--alert-warning)'),
        '--alert-info': resolveColor('var(--alert-info)'),
    }
};
const defaultDarkTheme = {
    name: 'Default dark',
    type: 'dark',
    colors: {
        /* Panel Colors */
        '--panel-left-bar': '#0B1F47',
        '--top-bar': '#242c3b',
        '--bottom-bar': '#242c3b',
        /* Text Colors */
        '--color-headings': '#FFFFFF',
        '--color-headings-blue': '#D6E9FF',
        '--color-body': '#D8D8E0',
        '--color-body-dark-mode': '#D8D8E0',
        '--color-light': '#FFFFFF',
        '--color-action': '#318FFF',
        '--color-action-hover': '#7AB6FF',
        '--color-disabled': '#636E82',
        '--color-information': '#B28AFF',
        '--color-success': '#5EC16A',
        '--color-warning': '#FFA347',
        '--color-error': '#FF6E6E',
        '--color-on-action': '#FFFFFF',
        '--color-on-disabled': '#D8D8E0',

        /* Surface Colors */
        '--surface-page': '#12161E',
        '--surface-notification': '#9D7AFF',
        '--surface-light': '#1D2B4D',
        '--surface-dark': '#030915',
        '--surface-dark-chat': '#0A0E14',
        "--surface-panel-dark": "#06122a",
        "--surface-dark-top": "#071531",
        '--surface-neutral': '#1E2532',
        '--surface-hover': '#2A3447',
        '--surface-primary': '#1E2532',
        '--surface-success': '#2A4D31',
        '--surface-disabled': '#3C4A63',
        '--surface-warning': '#5C3D10',
        '--surface-error': '#5C1A1A',
        '--surface-information': '#3A295C',
        '--surface-action': '#1D3A6D',
        '--surface-action-hover': '#244A8C',

        /* Icon Colors */
        '--icon-primary': '#0B1F47',
        '--icon-primary-dm': '#DECDFF',
        '--icon-light': '#FFFFFF',
        '--icon-hover': '#7AB6FF',
        '--icon-active': '#D6E9FF',
        '--icon-unactive': '#636E82',
        '--icon-information': '#B28AFF',
        '--icon-success': '#7DDF87',
        '--icon-warning': '#FFA347',
        '--icon-error': '#FF6E6E',

        /* Button Colors */
        '--btn-primary': '#0B1F47',
        '--btn-primary-dm': '#0B1F47',
        '--btn-hover': '#3C4C6C',
        '--btn-active': '#318FFF',
        '--btn-unactive': '#3C4A63',
        '--btn-information': '#B28AFF',
        '--btn-success': '#7DDF87',
        '--btn-warning': '#FFA347',
        '--btn-error': '#FF6E6E',

        /* Border Colors */
        '--border-primary': '#3C4A63',
        '--border-intermediary': '#636E82',
        '--border-information': '#B28AFF',
        '--border-success': '#7DDF87',
        '--border-warning': '#FFA347',
        '--border-error': '#FF6E6E',
        '--border-disabled': '#3C4A63',
        '--border-action': '#318FFF',
        '--border-action-hover': '#318FFF',
        '--border-focus': '#7AB6FF',
        '--border-light': 'rgba(60, 74, 99, 0.5)',

        /* Chat Bubble */
        '--color-chat-bubble': '#1D3A6D',

        /* Tag Colors */
        '--tag-01': '#318FFF',
        '--tag-02': '#2772CC',
        '--tag-03': '#2440BD',
        '--tag-04': '#1D3397',
        '--tag-05': '#9D7AFF',
        '--tag-06': '#6B4DCC',
        '--color-dark-hover': '#40404090',

        /* Legacy Mappings (for compatibility) */
        '--primary-main': '#318FFF',
        '--primary-contrastText': '#FFFFFF',
        '--secondary-main': '#7DDF87',
        '--secondary-contrastText': '#12161E',
        '--background-body': '#12161E',
        '--background-surface': '#1E2532',
        '--background-muted': '#1E2532',
        '--text-primary': '#D8D8E0',
        '--text-secondary': '#A0AEC0',
        '--text-action': '#7AB6FF',
        '--text-hover': '#9AC6FF',
        '--text-disabled': '#636E82',
        '--text-profile-name': '#FFFFFF',
        '--btn-primary-background': '#318FFF',
        '--btn-primary-text': '#FFFFFF',
        '--btn-primary-hover': '#7AB6FF',
        '--btn-primary-disabled': '#3C4A63',
        '--btn-secondary-background': '#5EC16A',
        '--btn-secondary-text': '#12161E',
        '--btn-secondary-hover': '#7DDF87',
        '--btn-secondary-disabled': '#3C4A63',
        '--alert-success': '#7DDF87',
        '--alert-error': '#FF6E6E',
        '--alert-warning': '#FFA347',
        '--alert-info': '#B28AFF'
    }
};


const FIXED_COLOR_OPTIONS_LIGHT = {
    /* panel Colors */
    '--panel-left-bar': ['#0B1F47', '#1D2B4D', '#242C3B'],
    '--top-bar': ['#FFFFFF', '#1D2B4D', '#242C3B'],
    '--bottom-bar': ['#FFFFFF', '#1D2B4D', '#242C3B'],

    /* Text Colors */
    '--color-headings': ['#242C3B', '#0B1F47', '#303B4F'],
    '--color-headings-blue': ['#0B1F47', '#1D3397', '#2440BD'],
    '--color-body': ['#303B4F', '#242C3B', '#636E82'],
    '--color-action': ['#318FFF', '#2772CC', '#2440BD'],
    '--color-action-hover': ['#2440BD', '#1D3397', '#0B1F47'],
    '--color-disabled': ['#636E82', '#3C4A63', '#A0AEC0'],
    '--color-information': ['#5929AB', '#7A54BC', '#9D7AFF'],
    '--color-success': ['#387440', '#5EC16A', '#7DDF87'],
    '--color-warning': ['#FF7B00', '#FFA347', '#FFB872'],
    '--color-error': ['#CB3030', '#FE3C3C', '#FF6E6E'],
    '--color-on-action': ['#FFFFFF', '#F5F5F5', '#E0E0E0'],
    '--color-on-disabled': ['#242C3B', '#303B4F', '#636E82'],
    '--color-light': ['FFFFFF'],

    /* Surface Colors */
    '--surface-page': ['#F1F2F4', '#E0E5EB', '#FFFFFF'],
    '--surface-notification': ['#7A54BC', '#9D7AFF', '#B28AFF'],
    '--surface-light': ['#D6E9FF', '#BAD6FF', '#7AB6FF'],
    '--surface-dark': ['#030915', '#1E2532', '#12161E'],
    '--surface-neutral': ['#F1F2F4', '#E0E5EB', '#D1D7DF'],
    '--surface-hover': ['#F1F2F4', '#E0E5EB', '#D1D7DF'],
    '--surface-primary': ['#FFFFFF', '#F8F9FA', '#EFF2F5'],
    '--surface-success': ['#DFF3E1', '#C8E6C9', '#A5D6A7'],
    '--surface-disabled': ['#D8D8E0', '#C5C5D2', '#B2B2C4'],
    '--surface-warning': ['#FFD8B8', '#FFCC99', '#FFBB7A'],
    '--surface-error': ['#FE5C5C', '#FF8A8A', '#FFA8A8'],
    '--surface-information': ['#DED4EE', '#C9BAE8', '#B5A0E2'],
    '--surface-action': ['#318FFF', '#2772CC', '#2440BD'],
    '--surface-action-hover': ['#2772CC', '#1D5AA8', '#134294'],

    /* Icon Colors */
    '--icon-primary': ['#3C4A63', '#2A3447', '#1E2532'],
    '--icon-hover': ['#318FFF', '#2772CC', '#2440BD'],
    '--icon-active': ['#0B1F47', '#1D2B4D', '#242C3B'],
    '--icon-information': ['#5929AB', '#7A54BC', '#9D7AFF'],
    '--icon-success': ['#5EC16A', '#4CAF50', '#388E3C'],
    '--icon-warning': ['#FF7B00', '#FF9800', '#FB8C00'],
    '--icon-error': ['#FE3C3C', '#F44336', '#E53935'],

    /* Button Colors */
    '--btn-primary': ['#0B1F47', '#1D2B4D', '#242C3B'],
    '--btn-hover': ['#3C4C6C', '#2A3447', '#1E2532'],
    '--btn-active': ['#318FFF', '#2772CC', '#2440BD'],
    '--btn-information': ['#5929AB', '#7A54BC', '#9D7AFF'],
    '--btn-success': ['#5EC16A', '#4CAF50', '#388E3C'],
    '--btn-warning': ['#FF7B00', '#FF9800', '#FB8C00'],
    '--btn-error': ['#FE3C3C', '#F44336', '#E53935'],
    '--btn-unactive': ['#D8D8E0'],
    /* Border Colors */
    '--border-primary': ['#D8D8E0', '#C5C5D2', '#B2B2C4'],
    '--border-information': ['#5929AB', '#7A54BC', '#9D7AFF'],
    '--border-success': ['#5EC16A', '#4CAF50', '#388E3C'],
    '--border-warning': ['#FF7B00', '#FF9800', '#FB8C00'],
    '--border-error': ['#FE3C3C', '#F44336', '#E53935'],
    '--border-focus': ['#318FFF', '#2772CC', '#2440BD'],
    '--border-action-hover': ['#318FFF', '#2772CC', '#2440BD'],

    /* Legacy Colors */
    '--primary-main': ['#318FFF', '#2772CC', '#2440BD'],
    '--primary-contrastText': ['#FFFFFF', '#F5F5F5', '#E0E0E0'],
    '--secondary-main': ['#5EC16A', '#4CAF50', '#388E3C'],
    '--background-body': ['#F1F2F4', '#E0E5EB', '#D1D7DF'],
    '--background-surface': ['#FFFFFF', '#F8F9FA', '#EFF2F5'],
    '--text-primary': ['#303B4F', '#242C3B', '#1E2532'],
    '--text-secondary': ['#636E82', '#3C4A63', '#2A3447'],
    '--text-profile-name': ['#0B1F47', '#3C4A63', '#2A3447'],

    '--btn-primary-background': ['#318FFF', '#2772CC', '#2440BD'],
    '--btn-primary-text': ['#FFFFFF', '#F5F5F5', '#E0E0E0'],
    '--alert-success': ['#5EC16A', '#4CAF50', '#388E3C'],
    '--alert-error': ['#FE3C3C', '#F44336', '#E53935'],
    '--alert-warning': ['#FF7B00', '#FF9800', '#FB8C00'],
    '--alert-info': ['#5929AB', '#7A54BC', '#9D7AFF']
};

const FIXED_COLOR_OPTIONS_DARK = {
    /* panel Colors */
    '--panel-left-bar': ['#0B1F47', '#1D2B4D', '#242C3B'],
    '--top-bar': ['#242c3b', '#1D2B4D', '#242C3B'],
    '--bottom-bar': ['#242c3b', '#1D2B4D', '#242C3B'],
    /* Text Colors */
    '--color-headings': ['#FFFFFF', '#D8D8E0', '#BEC8D8'],
    '--color-headings-blue': ['#D6E9FF', '#7AB6FF', '#9AC6FF'],
    '--color-body': ['#D8D8E0', '#A0AEC0', '#7E8CA0'],
    '--color-action': ['#318FFF', '#7AB6FF', '#9AC6FF'],
    '--color-action-hover': ['#7AB6FF', '#9AC6FF', '#BAD6FF'],
    '--color-disabled': ['#636E82', '#3C4A63', '#2A3447'],
    '--color-information': ['#B28AFF', '#9D7AFF', '#7A54BC'],
    '--color-success': ['#5EC16A', '#7DDF87', '#9CE9A7'],
    '--color-warning': ['#FFA347', '#FFB872', '#FFCC99'],
    '--color-error': ['#FF6E6E', '#FF8E8E', '#FFAEAE'],
    '--color-on-action': ['#FFFFFF', '#F5F5F5', '#E0E0E0'],
    '--color-on-disabled': ['#D8D8E0', '#A0AEC0', '#7E8CA0'],
    '--color-light': ['FFFFFF'],

    /* Surface Colors */
    '--surface-page': ['#12161E', '#0A0E14', '#1E2532'],
    '--surface-notification': ['#9D7AFF', '#B28AFF', '#DECDFF'],
    '--surface-light': ['#1D2B4D', '#2A3447', '#3C4A63'],
    '--surface-dark': ['#030915', '#1E2532', '#12161E'],
    '--surface-neutral': ['#1E2532', '#2A3447', '#3C4A63'],
    '--surface-hover': ['#2A3447', '#3C4A63', '#4E5C7A'],
    '--surface-primary': ['#1E2532', '#2A3447', '#3C4A63'],
    '--surface-success': ['#2A4D31', '#387440', '#459C4F'],
    '--surface-disabled': ['#3C4A63', '#2A3447', '#1E2532'],
    '--surface-warning': ['#5C3D10', '#7A4F1A', '#986124'],
    '--surface-error': ['#5C1A1A', '#7A2323', '#982C2C'],
    '--surface-information': ['#3A295C', '#5929AB', '#7A54BC'],
    '--surface-action': ['#1D3A6D', '#244A8C', '#2B5AAB'],
    '--surface-action-hover': ['#244A8C', '#2B5AAB', '#326ACA'],

    /* Icon Colors */
    '--icon-primary': ['#DECDFF', '#B28AFF', '#9D7AFF'],
    '--icon-hover': ['#7AB6FF', '#9AC6FF', '#BAD6FF'],
    '--icon-active': ['#D6E9FF', '#7AB6FF', '#318FFF'],
    '--icon-information': ['#B28AFF', '#9D7AFF', '#7A54BC'],
    '--icon-success': ['#7DDF87', '#5EC16A', '#4CAF50'],
    '--icon-warning': ['#FFA347', '#FF7B00', '#FB8C00'],
    '--icon-error': ['#FF6E6E', '#FE3C3C', '#F44336'],

    /* Button Colors */
    '--btn-primary': ['#0c1e47', '#B28AFF', '#9D7AFF'],
    '--btn-hover': ['#318fff', '#9D7AFF', '#7A54BC'],
    '--btn-active': ['#7AB6FF', '#9AC6FF', '#BAD6FF'],
    '--btn-information': ['#B28AFF', '#9D7AFF', '#7A54BC'],
    '--btn-success': ['#7DDF87', '#5EC16A', '#4CAF50'],
    '--btn-warning': ['#FFA347', '#FF7B00', '#FB8C00'],
    '--btn-error': ['#FF6E6E', '#FE3C3C', '#F44336'],

    /* Border Colors */
    '--border-primary': ['#3C4A63', '#2A3447', '#1E2532'],
    '--border-secondary': ['#318FFF', '#7AB6FF', '#9AC6FF'],
    '--border-information': ['#B28AFF', '#9D7AFF', '#7A54BC'],
    '--border-success': ['#7DDF87', '#5EC16A', '#4CAF50'],
    '--border-warning': ['#FFA347', '#FF7B00', '#FB8C00'],
    '--border-error': ['#FF6E6E', '#FE3C3C', '#F44336'],
    '--border-focus': ['#7AB6FF', '#9AC6FF', '#BAD6FF'],
    '--btn-unactive': ['#D8D8E0'],

    /* Legacy Colors */
    '--primary-main': ['#318FFF', '#7AB6FF', '#9AC6FF'],
    '--primary-contrastText': ['#FFFFFF', '#F5F5F5', '#E0E0E0'],
    '--secondary-main': ['#7DDF87', '#5EC16A', '#4CAF50'],
    '--background-body': ['#12161E', '#0A0E14', '#1E2532'],
    '--background-surface': ['#1E2532', '#2A3447', '#3C4A63'],
    '--text-primary': ['#D8D8E0', '#A0AEC0', '#7E8CA0'],
    '--text-secondary': ['#A0AEC0', '#7E8CA0', '#5C6A7E'],
    '--text-profile-name': ['#FFFFFF', '#3C4A63', '#2A3447'],

    '--btn-primary-background': ['#318FFF', '#7AB6FF', '#9AC6FF'],
    '--btn-primary-text': ['#FFFFFF', '#F5F5F5', '#E0E0E0'],
    '--alert-success': ['#7DDF87', '#5EC16A', '#4CAF50'],
    '--alert-error': ['#FF6E6E', '#FE3C3C', '#F44336'],
    '--alert-warning': ['#FFA347', '#FF7B00', '#FB8C00'],
    '--alert-info': ['#B28AFF', '#9D7AFF', '#7A54BC']
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
    // Initialize state from localStorage or defaults
    const [themeMode, setThemeMode] = useState(() => {
        const savedMode = localStorage.getItem('theme');
        return savedMode || 'light';
    });

    const [currentTheme, setCurrentTheme] = useState(() => {
        const savedTheme = localStorage.getItem('currentTheme');
        const savedMode = localStorage.getItem('theme') || 'light';

        // If no saved theme, use the appropriate default
        if (!savedTheme) {
            return savedMode === 'light' ? defaultLightTheme : defaultDarkTheme;
        }

        const parsedTheme = JSON.parse(savedTheme);

        // Merge with current defaults to ensure all colors are set
        const defaultTheme = parsedTheme.type === 'light' ? defaultLightTheme : defaultDarkTheme;
        return {
            ...defaultTheme,
            colors: {
                ...defaultTheme.colors,
                ...parsedTheme.colors
            }
        };
    });

    const [customThemes, setCustomThemes] = useState(() => {
        const savedThemes = localStorage.getItem('customThemes');
        return savedThemes ? JSON.parse(savedThemes) : [];
    });

    // Memoize fixed color options based on theme mode
    const fixedColorOptions = useCallback(
        () => themeMode === 'light' ? FIXED_COLOR_OPTIONS_LIGHT : FIXED_COLOR_OPTIONS_DARK,
        [themeMode]
    );

    // Apply theme and persist to localStorage
    const applyTheme = useCallback((theme) => {
        const root = document.querySelector('#root');

        // Remove all theme classes
        root.classList.remove('light', 'dark');

        // Add current theme class
        root.classList.add(theme.type);

        // Set CSS variables on root element
        Object.entries(theme.colors).forEach(([key, value]) => {
            root.style.setProperty(key, value);
        });

        // Persist theme
        setCurrentTheme(theme);
        setThemeMode(theme.type);
        localStorage.setItem('currentTheme', JSON.stringify(theme));
        localStorage.setItem('theme', theme.type);
    }, []);

    // Apply theme on initial load
    useEffect(() => {
        applyTheme(currentTheme);
    }, []);

    // Handle color updates
    const updateThemeColor = useCallback((colorVar, colorValue) => {
        const updatedTheme = {
            ...currentTheme,
            colors: {
                ...currentTheme.colors,
                [colorVar]: colorValue
            }
        };
        applyTheme(updatedTheme);
    }, [currentTheme, applyTheme]);

    // Persist custom themes to localStorage when they change
    useEffect(() => {
        localStorage.setItem('customThemes', JSON.stringify(customThemes));
    }, [customThemes]);

    // Toggle between light/dark
    const toggleThemeMode = useCallback(() => {
        const newThemeMode = themeMode === 'light' ? 'dark' : 'light';
        const defaultTheme = newThemeMode === 'light' ? defaultLightTheme : defaultDarkTheme;

        // Create new theme that merges defaults with customizations
        const newTheme = {
            ...defaultTheme,
            colors: {
                ...defaultTheme.colors,
                ...Object.fromEntries(
                    Object.entries(currentTheme.colors)
                        .filter(([key, value]) => {
                            const originalDefault = themeMode === 'light'
                                ? defaultLightTheme.colors[key]
                                : defaultDarkTheme.colors[key];
                            return value !== originalDefault;
                        })
                )
            }
        };

        applyTheme(newTheme);
    }, [themeMode, currentTheme, defaultLightTheme, defaultDarkTheme, applyTheme]);

    // Select specific theme mode (light/dark)
    const selectTheme = useCallback((type) => {
        localStorage.setItem("theme", type);

        if (type === currentTheme.type) return; // No change needed

        const defaultTheme = type === 'light' ? defaultLightTheme : defaultDarkTheme;

        // Create new theme that merges defaults with customizations
        const newTheme = {
            ...defaultTheme,
            colors: {
                ...defaultTheme.colors,
                ...Object.fromEntries(
                    Object.entries(currentTheme.colors)
                        .filter(([key, value]) => {
                            const originalDefault = currentTheme.type === 'light'
                                ? defaultLightTheme.colors[key]
                                : defaultDarkTheme.colors[key];
                            return value !== originalDefault;
                        })
                )
            }
        };

        applyTheme(newTheme);
    }, [currentTheme, applyTheme]);

    const saveCurrentTheme = useCallback((themeName) => {
        if (!themeName.trim()) return;

        const newCustomTheme = {
            ...currentTheme,
            name: themeName.trim(),
            lastUpdated: new Date().toISOString()
        };

        setCustomThemes(prevThemes => {
            const existingIndex = prevThemes.findIndex(t => t.name === themeName.trim());
            if (existingIndex >= 0) {
                // Update existing theme
                const updatedThemes = [...prevThemes];
                updatedThemes[existingIndex] = newCustomTheme;
                return updatedThemes;
            } else {
                // Add new theme
                return [...prevThemes, newCustomTheme];
            }
        });

        applyTheme(newCustomTheme);
    }, [currentTheme, applyTheme]);

    const deleteTheme = useCallback((themeName) => {
        setCustomThemes(prevThemes => prevThemes.filter(t => t.name !== themeName));
        if (currentTheme.name === themeName) {
            const defaultTheme = themeMode === 'light' ? defaultLightTheme : defaultDarkTheme;
            applyTheme(defaultTheme);
        }
    }, [currentTheme.name, themeMode, applyTheme]);

    const resetToDefault = useCallback(() => {
        const defaultTheme = themeMode === 'light' ? defaultLightTheme : defaultDarkTheme;
        applyTheme(defaultTheme);
    }, [themeMode, applyTheme]);

    const getColorOptions = useCallback((colorVar) => {
        // Check fixed options first
        const options = fixedColorOptions()[colorVar];
        if (options) return options;

        // Fallback to current theme value
        return currentTheme.colors[colorVar] ? [currentTheme.colors[colorVar]] : ['#808080'];
    }, [currentTheme.colors, fixedColorOptions]);

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        currentTheme,
        customThemes,
        themeMode: currentTheme.type,
        toggleThemeMode,
        selectTheme, // Added selectTheme function
        updateTheme: updateThemeColor,
        saveTheme: saveCurrentTheme,
        applyTheme,
        deleteTheme,
        resetToDefault,
        defaultLightTheme,
        defaultDarkTheme,
        getColorOptions,
        fixedColorOptions: currentTheme.type === 'light'
            ? FIXED_COLOR_OPTIONS_LIGHT
            : FIXED_COLOR_OPTIONS_DARK
    }), [
        currentTheme,
        customThemes,
        toggleThemeMode,
        selectTheme,
        updateThemeColor,
        saveCurrentTheme,
        applyTheme,
        deleteTheme,
        resetToDefault,
        getColorOptions
    ]);

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};